import React, { useState } from 'react';

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from '@mui/material';
import { format } from 'sql-formatter';

import { logger } from '@/utils/logger';

interface SqlEditorDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (sql: string) => void;
  initialSql: string;
}

/**
 * Dialog for editing SQL queries in recipes
 */
export const SqlEditorDialog: React.FC<SqlEditorDialogProps> = ({
  open,
  onClose,
  onSave,
  initialSql,
}) => {
  const [sql, setSql] = useState(initialSql);

  React.useEffect(() => {
    if (open) {
      setSql(initialSql);
    }
  }, [open, initialSql]);

  const handleFormat = () => {
    try {
      const formatted = format(sql, { language: 'sql', tabWidth: 2 });
      setSql(formatted);
    } catch (error) {
      logger.error('Failed to format SQL:', error);
    }
  };

  const handleSave = () => {
    onSave(sql);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Edit SQL Query
        <Button
          size="small"
          variant="outlined"
          onClick={handleFormat}
          sx={{ position: 'absolute', right: 60, top: 10 }}
        >
          Format
        </Button>
      </DialogTitle>
      <DialogContent sx={{ p: 2 }}>
        <TextField
          fullWidth
          multiline
          rows={15}
          value={sql}
          onChange={(e) => setSql(e.target.value)}
          variant="outlined"
          sx={{
            fontFamily: 'monospace',
            '& .MuiInputBase-input': {
              fontFamily: 'monospace',
              fontSize: 13,
            },
          }}
        />
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" color="error" onClick={onClose}>
          Cancel
        </Button>
        <Button onClick={handleSave} variant="contained" color="primary">
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};
